import { NgIf } from '@angular/common'
import { Component } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import {
    BaseComponent,
    FeatureType,
    Grid,
    LeadershipTeamMember,
    LeadershipTeamVisit,
    ListItem,
    ModalSize,
    resolveCanonicalListValue,
    VpJob,
} from '../../shared'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { LeadershipTeamVisitModalComponent } from './leadership-team-visit-modal.component'

@Component({
    selector: 'app-leadership-team-visits-list',
    templateUrl: './leadership-team-visits-list.component.html',
    standalone: true,
    imports: [
        FormsModule,
        NgIf,
        FaIconComponent,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class LeadershipTeamVisitsListComponent extends BaseComponent {
    faXmark = faXmark
    grid: Grid<LeadershipTeamVisit>
    allJobs: VpJob[] = []
    jobMap: ListItem<string>[] = []
    leadershipMembers: LeadershipTeamMember[] = []
    memberMap: ListItem<string>[] = []

    constructor() {
        super()
        this.grid = new Grid<LeadershipTeamVisit>(this.services)
            .setupModalEditing(
                this.services.data.leadershipTeamVisits,
                'Leadership Team Visit',
                LeadershipTeamVisitModalComponent,
                () => LeadershipTeamVisit.createNew(),
                this,
                ModalSize.large,
            )
            .filterBy(item => [item.jobName, item.job, item.visitedByName, item.notes, item.observations, item.safetyFeedback])
            .wrapColumnHeaders()

        this.grid.modified.on(this.refreshItems.bind(this))
        this.refresh()
    }

    refresh() {
        this.loadData().subscribe({
            next: result => this.applyLoadedData(result),
        })
    }

    private loadData() {
        return this.services.data.load(
            true,
            true,
            this.services.data.vpJobs.queryAll(),
            this.services.data.get<LeadershipTeamMember[]>('/api/LeadershipTeamVisit/GetLeadershipMembers'),
            this.services.data.leadershipTeamVisits.queryAll(),
        )
    }

    private applyLoadedData(result: unknown[]) {
        this.allJobs = result[0] as VpJob[]
        this.leadershipMembers = result[1] as LeadershipTeamMember[]
        const items = result[2] as LeadershipTeamVisit[]
        this.jobMap = this.allJobs
            .filter(j => j.jobStatus === 1)
            .orderByDescending((x: any) => x.id)
            .map(j => new ListItem(j.id, j.id + ' ' + j.name))
        this.memberMap = this.leadershipMembers.map(m => new ListItem(m.id, m.displayName))
        this.assignJobDisplayNames(items)
        this.grid.items = items
        this.initialLoadingComplete()
    }

    private ensureJobMapLoaded(): Promise<void> {
        if (this.jobMap.length > 0) {
            return Promise.resolve()
        }
        return firstValueFrom(this.loadData()).then(result => {
            this.applyLoadedData(result)
        })
    }

    openAddForJob(jobId: string, jobName: string): Promise<any> | null {
        if (!this.canEdit) {
            return null
        }
        return this.ensureJobMapLoaded().then(() => {
            const visit = LeadershipTeamVisit.createNew()
            visit.job = resolveCanonicalListValue(jobId, this.jobMap.map(j => j.value)) ?? jobId
            visit.jobName = jobName
            return this.grid.addItemWith(visit)
        })
    }

    private refreshItems() {
        this.services.data.leadershipTeamVisits.queryAll().subscribe({
            next: items => {
                this.assignJobDisplayNames(items)
                this.grid.items = items
            },
        })
    }

    private assignJobDisplayNames(items: LeadershipTeamVisit[]) {
        for (const item of items) {
            if (item.jobName && item.jobName !== item.job) {
                item.jobName = `${item.job.trim()} ${item.jobName}`
            } else {
                item.jobName = item.job?.trim() ?? ''
            }
        }
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.OperationsLeadershipTeamVisitsEdit])
    }

    deleteSelectedItem() {
        const selected = this.grid.selectedItem
        if (!selected) return
        this.services.modals.confirm('Delete', 'Are you sure you want to delete this leadership team visit?').then(() => {
            this.services.data.leadershipTeamVisits.delete(selected).subscribe({
                next: () => this.refreshItems(),
            })
        })
    }
}
